import { useQuery } from "@tanstack/react-query";
import { qualityChecklistsApi } from "@/app/lib/api/qualityChecklists";
import {
  QualityChecklistQuestion,
  QualityChecklistPdfReference,
  ProjectDocument
} from "@/app/lib/supabase/types";
import { useAppStore } from "@/app/lib/stores/useAppStore";

const STALE_TIME = 5 * 60 * 1000; // 5 minute
const GC_TIME = 30 * 60 * 1000; // 30 minute

export const qualityChecklistQueryKeys = {
  all: ["qualityChecklists"] as const,
  questions: {
    all: () => [...qualityChecklistQueryKeys.all, "questions"] as const,
    byCompany: (companyId: string) =>
      [...qualityChecklistQueryKeys.questions.all(), "company", companyId] as const,
    detail: (questionId: string) =>
      [...qualityChecklistQueryKeys.questions.all(), "detail", questionId] as const,
    byProductionStage: (productionStageId: string) =>
      [...qualityChecklistQueryKeys.questions.all(), "productionStage", productionStageId] as const,
  },
  pdfReferences: {
    all: () => [...qualityChecklistQueryKeys.all, "pdfReferences"] as const,
    byChecklistItem: (checklistItemId: string) =>
      [...qualityChecklistQueryKeys.pdfReferences.all(), "checklistItem", checklistItemId] as const,
    byDocument: (documentId: string) =>
      [...qualityChecklistQueryKeys.pdfReferences.all(), "document", documentId] as const,
    withDocumentsByProductionStage: (productionStageId: string) =>
      [...qualityChecklistQueryKeys.pdfReferences.all(), "withDocuments", "productionStage", productionStageId] as const,
  },
};

// Quality Checklist Questions hooks
export function useQualityChecklistQuestions() {
  const { selectedCompanyId } = useAppStore();

  return useQuery<QualityChecklistQuestion[]>({
    queryKey: qualityChecklistQueryKeys.questions.byCompany(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return qualityChecklistsApi.getQuestions(selectedCompanyId);
    },
    enabled: !!selectedCompanyId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useQualityChecklistQuestion(questionId: string | undefined) {
  return useQuery<QualityChecklistQuestion>({
    queryKey: qualityChecklistQueryKeys.questions.detail(questionId || ""),
    queryFn: () => {
      if (!questionId) throw new Error("No question ID provided");
      return qualityChecklistsApi.getQuestion(questionId);
    },
    enabled: !!questionId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// Quality Checklist Items hooks
export function useQualityChecklistQuestionsByProductionStage(productionStageId: string | undefined) {
  return useQuery<QualityChecklistQuestion[]>({
    queryKey: qualityChecklistQueryKeys.questions.byProductionStage(productionStageId || ""),
    queryFn: () => {
      if (!productionStageId) throw new Error("No production stage ID provided");
      return qualityChecklistsApi.getChecklistQuestionsByProductionStage(productionStageId);
    },
    enabled: !!productionStageId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// PDF References hooks
export function useQualityChecklistPdfReferences(checklistItemId: string | undefined) {
  return useQuery<QualityChecklistPdfReference[]>({
    queryKey: qualityChecklistQueryKeys.pdfReferences.byChecklistItem(checklistItemId || ""),
    queryFn: () => {
      if (!checklistItemId) throw new Error("No checklist item ID provided");
      return qualityChecklistsApi.getPdfReferencesByChecklistQuestion(checklistItemId);
    },
    enabled: !!checklistItemId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useQualityChecklistPdfReferencesByDocument(documentId: string | undefined) {
  return useQuery<QualityChecklistPdfReference[]>({
    queryKey: qualityChecklistQueryKeys.pdfReferences.byDocument(documentId || ""),
    queryFn: () => {
      if (!documentId) throw new Error("No document ID provided");
      return qualityChecklistsApi.getPdfReferencesByDocument(documentId);
    },
    enabled: !!documentId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function usePdfReferencesAndDocumentsByProductionStage(productionStageId: string | undefined) {
  return useQuery<{
    pdfReferences: QualityChecklistPdfReference[];
    pdfDocuments: ProjectDocument[];
  }>({
    queryKey: qualityChecklistQueryKeys.pdfReferences.withDocumentsByProductionStage(productionStageId || ""),
    queryFn: () => {
      if (!productionStageId) throw new Error("No production stage ID provided");
      return qualityChecklistsApi.getPdfReferencesAndDocumentsByProductionStage(productionStageId);
    },
    enabled: !!productionStageId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}